import type { IAuthFormFields, ICognitoError } from '@/types';
import { COGNITO_ERRORS, ROUTE_PATHS } from '@/constants';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Container,
  Text,
  useControllableState,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Section } from '../components/Section';
import { PlainLayout } from '../layout';
import { Auth } from '@aws-amplify/auth';
import { LoginForm } from '../components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pathJoin } from '@/utils';
import { useState } from 'react';

export const UserLogin: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const activationCode: string | null = searchParams.get('code');
  const username: string | null = searchParams.get('email');
  const [loginError, setLoginError] = useControllableState({ defaultValue: '' });
  // eslint-disable-next-line use-encapsulation/prefer-custom-hooks
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const goToPage = (replace = false, path: string): void => {
    navigate(path, { replace });
  };

  if (username && activationCode) {
    const confirmSignUp = async (username: string, code: string): Promise<void> => {
      try {
        await Auth.confirmSignUp(username, code);
      } catch (error) {
        console.log('error confirming sign up', error);
      }
    };

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    confirmSignUp(username, activationCode);
  }

  const handleLoginFormSubmit = async ({ email, password }: IAuthFormFields): Promise<void> => {
    try {
      setLoginError('');
      setIsLoading(true);
      await Auth.signIn(email.toLowerCase(), password);
      setIsLoading(false);
      const path = pathJoin([`/${ROUTE_PATHS.user}`, ROUTE_PATHS.userDashboard]);
      goToPage(true, path);
    } catch (error) {
      const msg = COGNITO_ERRORS[(error as ICognitoError).code];
      setLoginError(msg);
    }

    setIsLoading(false);
  };

  return (
    <PlainLayout>
      <Box bg="brand.sand" px={{ base: '15px', md: 0 }} py={{ base: 8, md: 16 }} w="full">
        <Container bg="white" minW="75%" px={{ base: '5%', md: '22%' }} py="5%">
          <Section
            containerProps={{ py: 0 }}
            headingProps={{ mx: 'auto' }}
            id="login-form"
            title={t('LOGIN.LOGIN_TITLE')}
          >
            {loginError && (
              <Alert mb={8} status="error">
                <AlertIcon />
                <AlertDescription>{t(loginError)}</AlertDescription>
              </Alert>
            )}
            {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
            <LoginForm isLoading={isLoading} onSubmit={handleLoginFormSubmit} />
            <Text fontStyle="italic" textAlign="center">
              {t('LOGIN.OR')}
            </Text>
            <Button as="a" h="50px" href={ROUTE_PATHS.fullSignUp} variant="mbeSecondary">
              {t('LOGIN.SIGNUP')}
            </Button>
          </Section>
        </Container>
      </Box>
    </PlainLayout>
  );
};
